import { SkyButton, SkyInput, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { CredentialExpiryView } from "@shared/types/credentials";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { expirySentence } from "./CredentialStatus";

/**
 * "Expires on" for a credential that carries no expiry of its own: a client secret, an API token,
 * a key pair (2026-09-28 cred-check design, "UI"). The date feeds the warnings 30 and 7 days
 * ahead; it never changes the credential. A date the token carries itself (a SAS's `se`) is shown
 * read-only by the caller instead.
 *
 * A native date field, not `SkyDatePicker`: the 2.4.0 picker fails axe `aria-allowed-attr` on its
 * popover trigger (`ScheduleSkipDates.tsx`). A failure stays inline, where the control remains.
 */
export function CredentialExpiryEditor({
  label,
  expiry,
  canEdit,
  pending,
  onSave,
  testId,
}: {
  /** The field's words, for the accessible name: "Client secret expires on". */
  label: string;
  expiry: CredentialExpiryView | null;
  canEdit: boolean;
  pending: boolean;
  onSave: (expiresOn: string | null) => Promise<unknown>;
  testId: string;
}) {
  const saved = expiry ? expiry.expiresAt.slice(0, 10) : "";
  // The typed day belongs to the saved date it was typed over: when the saved date changes (a
  // save, a refetch), the field shows the new one rather than a stale draft.
  const [draft, setDraft] = useState({ over: saved, value: saved });
  const value = draft.over === saved ? draft.value : saved;
  const setValue = (next: string) => setDraft({ over: saved, value: next });
  const inline = useInlineError();
  const notice = useNotice();

  const save = async (next: string | null) => {
    inline.clear();
    try {
      await onSave(next);
      notice.success(next ? `${label}: expiry date saved.` : `${label}: expiry date cleared.`);
    } catch (err) {
      inline.show(err, "The expiry date could not be saved.");
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {expiry ? (
        <SkyText size={200} color="muted" block testId={`${testId}-current`}>
          {expirySentence(expiry)}
        </SkyText>
      ) : null}
      {canEdit ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-56">
            <SkyInput
              type="date"
              label={`${label} expires on`}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              disabled={pending}
              hint="Warnings go out 30 and 7 days before this date."
              testId={`${testId}-input`}
            />
          </div>
          <SkyButton
            appearance="secondary"
            disabled={pending || value === "" || value === saved}
            loading={pending}
            onClick={() => void save(value)}
            testId={`${testId}-save`}
          >
            Save date
          </SkyButton>
          {expiry ? (
            <SkyButton
              appearance="subtle"
              disabled={pending}
              onClick={() => void save(null)}
              testId={`${testId}-clear`}
            >
              Clear
            </SkyButton>
          ) : null}
        </div>
      ) : null}
      <InlineErrorBar error={inline.error} onDismiss={inline.clear} testId={`${testId}-error`} />
    </div>
  );
}
